<template>
  <div class="receipt-container">
    <div id="printable-receipt" class="bg-white mx-auto font-mono text-[12px] leading-tight font-bold text-black" style="width: 58mm; max-width: 58mm;">
      <div class="p-2">
        <!-- Header with Logo -->
        <div class="text-center mb-3">
          <div class="text-lg font-black text-black">IGW Digital Printing Service</div>
          <div class="font-bold text-black">27 A Dacudao Ave, Poblacion District</div>
          <div class="font-bold text-black">Davao City, Philippines</div>
          <div class="font-bold text-black">Tel: 0927-426-2634</div>
          <div class="font-bold text-black">VAT REG: {{ payment?.vat_reg || '707-950-003-00001' }}</div>
        </div>

        <!-- Receipt Info -->
        <div class="mb-3">
          <div class="text-center mb-2">
            <div class="font-black text-black">PAYMENT RECEIPT</div>
            <div class="font-bold text-black">{{ formatDate(payment?.payment_date) }}</div>
          </div>
          <div>
            <div class="font-bold text-black">Receipt #: {{ payment?.id || '-' }}</div>
            <div class="font-bold text-black">Cashier: {{ getCashierName() }}</div>
          </div>
        </div>

        <!-- Order Info -->
        <div class="mb-3">
            <div class="font-bold text-black">Order #: {{ payment?.order?.order_number || '-' }}</div>
            <div class="font-bold text-black">Client: {{ payment?.order?.client?.name || '-' }}</div>
          </div>

        <div class="text-center font-black text-black">================================</div>

        <!-- Order Items -->
        <div class="my-3">
          <div class="font-black mb-1 text-black">ORDER ITEMS:</div>
          <div v-if="payment?.order?.old_products" class="mb-1">
            <div class="font-bold text-black">{{ payment.order.old_products }}</div>
            <div class="flex justify-between text-[11px] font-bold text-black">
              <span>1 x ₱{{ formatPrice(payment.order.total_amount) }}</span>
              <span>₱{{ formatPrice(payment.order.total_amount) }}</span>
            </div>
          </div>
          <div v-else-if="payment?.order?.items?.length" v-for="item in payment.order.items" :key="item.id" class="mb-1">
            <div class="font-bold text-black">{{ item.product_name }}</div>
            <div class="flex justify-between text-[11px] font-bold text-black">
              <span>{{ item.quantity }} x ₱{{ formatPrice(calculateAdjustedUnitPrice(item)) }}</span>
              <span>₱{{ formatPrice(calculateItemTotal(item)) }}</span>
            </div>
          </div>
        </div>

        <div class="text-center font-black text-black">================================</div>

        <!-- Payment Details -->
        <div class="my-3">
          <div class="font-bold text-black">Payment Method: {{ formatPaymentMethod(payment?.payment_method) }}</div>
            <div v-if="payment?.transaction_reference" class="font-bold text-black">Ref #: {{ payment?.transaction_reference }}</div>
          <div class="font-black mt-2">
            <div class="flex justify-between text-[12px] text-black">
            <span>AMOUNT PAID:</span>
            <span>₱{{ formatPrice(payment?.amount) }}</span>
            </div>
          </div>
        </div>

        <div class="text-center font-black text-black">================================</div>

        <!-- Order Summary -->
        <div class="my-3 text-[11px] font-bold text-black">
          <div class="flex justify-between">
            <span>Order Total:</span>
            <span>₱{{ formatPrice(payment?.order?.total_amount || 0) }}</span>
          </div>
          <div class="flex justify-between">
            <span>Previous Payment:</span>
            <span>₱{{ formatPrice(payment?.previous_payment || 0) }}</span>
          </div>
          <div class="flex justify-between font-black">
            <span>Balance:</span>
            <span>₱{{ formatPrice(calculateRemainingBalance()) }}</span>
          </div>
        </div>

        <!-- Notes -->
        <template v-if="payment?.notes">
          <div class="text-center font-black text-black">================================</div>
          <div class="my-3">
            <div class="font-black text-black">Notes:</div>
            <div class="whitespace-pre-wrap font-bold text-black">{{ payment.notes }}</div>
          </div>
        </template>

        <div class="text-center font-black text-black">================================</div>

        <!-- Footer -->
        <div class="text-center mt-3">
          <div class="font-black mb-1 text-black">*** THANK YOU ***</div>
          <div class="font-bold text-black">Please keep this receipt</div>
          <div class="font-bold text-black">for your records</div>
          
          <!-- QR Code for Google Review -->
          <div class="mt-3 mb-4 flex flex-col items-center">
            <div class="text-[10px] font-bold mb-1 text-black">Scan to leave us a review!</div>
            <div class="w-[80px] h-[80px]">
              <QRCode 
                :value="GOOGLE_REVIEW_URL"
                :size="80"
                level="M"
                render-as="svg"
                class="mx-auto"
                :margin="1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end gap-3 mt-4 print:hidden">
      <button
        @click="downloadPDF"
        class="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded hover:bg-green-700 dark:hover:bg-green-600 flex items-center gap-2"
      >
        <i class="fas fa-file-pdf"></i>
        Download PDF
      </button>
      <button
        @click="printReceipt"
        class="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-2"
      >
        <i class="fas fa-print"></i>
        Print Receipt
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate, formatPrice } from '@/utils/formatters';
import QRCode from 'qrcode.vue';
import { useAuth } from '@/composables/useAuth';
import { calculateAdjustedUnitPrice, calculateItemTotal } from '@/utils/orderCalculations';

const { user } = useAuth();
const props = defineProps({
  payment: {
    type: Object,
    required: true
  }
});

const getCashierName = () => {
  // Use the created_by_name from the payment object
  if (props.payment?.created_by_name) {
    return props.payment.created_by_name;
  }
  
  // If not found in payment, use the current user's name
  if (user.value?.first_name && user.value?.last_name) {
    return `${user.value.first_name} ${user.value.last_name}`;
  }
  
  // Fallback to just first name if last name is not available
  if (user.value?.first_name) {
    return user.value.first_name;
  }
  
  // Final fallback
  return 'SUPERVISOR';
};



const formatPaymentMethod = (method) => {
  if (!method) return '-';
  return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
};

const calculateRemainingBalance = () => {
  if (!props.payment?.order?.total_amount) return 0;
  
  const orderTotal = Number(props.payment.order.total_amount) || 0;
  const previousPayment = Number(props.payment.previous_payment) || 0;
  const currentPayment = Number(props.payment.amount) || 0;
  
  console.log('Balance calculation:', {
    orderTotal,
    previousPayment,
    currentPayment
  });
  
  return Math.max(0, orderTotal - (previousPayment + currentPayment));
};

const printReceipt = async () => {
  try {
    // Generate PDF data
    const element = document.getElementById('printable-receipt');
    
    // Add extra padding at the bottom to ensure QR code fits
    element.style.paddingBottom = '20px';
    
    const canvas = await html2canvas(element, {
      scale: 4,
      logging: false,
      width: 219,
      windowWidth: 219,
      useCORS: true,
      backgroundColor: '#ffffff',
      imageTimeout: 0,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = `
          @font-face {
            font-family: 'Courier New';
            src: local('Courier New');
          }
          #printable-receipt {
            font-family: 'Courier New', monospace !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding-bottom: 20px !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    });
    
    // Reset the padding after canvas generation
    element.style.paddingBottom = '';
    
    // Calculate dimensions with extra space for QR code
    const imgWidth = 58;
    const pageHeight = Math.max((canvas.height * imgWidth) / canvas.width, imgWidth * 2);
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [imgWidth, pageHeight],
      compress: true,
      hotfixes: ['px_scaling']
    });

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0,
      0,
      imgWidth,
      pageHeight,
      undefined,
      'FAST'
    );

    pdf.setProperties({
      title: `Receipt #${props.payment?.id || 'unknown'}`,
      subject: 'Payment Receipt',
      creator: 'IGW Digital Printing Service',
      author: 'IGW Digital Printing Service'
    });

    // Instead of saving, open in new window and print
    const pdfData = pdf.output('bloburl');
    const printWindow = window.open(pdfData, '_blank');
    
    if (printWindow) {
      printWindow.onafterprint = () => {
        // Close the window after actual printing is done
        printWindow.close();
      };
      
      printWindow.onload = () => {
        try {
          printWindow.focus(); // Focus the window
          printWindow.print(); // Open print dialog
        } catch (error) {
          console.error('Error in print window:', error);
          // Keep window open if print fails
        }
      };
    } else {
      // If popup was blocked, just open the PDF in the current tab
      window.location.href = pdfData;
    }
  } catch (error) {
    console.error('Error printing receipt:', error);
    toast.error('Failed to print receipt');
  }
};

const downloadPDF = async () => {
  try {
    // Generate PDF data
    const element = document.getElementById('printable-receipt');
    
    // Add extra padding at the bottom to ensure QR code fits
    element.style.paddingBottom = '20px';
    
    const canvas = await html2canvas(element, {
      scale: 4,
      logging: false,
      width: 219,
      windowWidth: 219,
      useCORS: true,
      backgroundColor: '#ffffff',
      imageTimeout: 0,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.innerHTML = `
          @font-face {
            font-family: 'Courier New';
            src: local('Courier New');
          }
          #printable-receipt {
            font-family: 'Courier New', monospace !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding-bottom: 20px !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    });
    
    // Reset the padding after canvas generation
    element.style.paddingBottom = '';
    
    // Calculate dimensions with extra space for QR code
    const imgWidth = 58;
    const pageHeight = Math.max((canvas.height * imgWidth) / canvas.width, imgWidth * 2);
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [imgWidth, pageHeight],
      compress: true,
      hotfixes: ['px_scaling']
    });

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0,
      0,
      imgWidth,
      pageHeight,
      undefined,
      'FAST'
    );

    pdf.setProperties({
      title: `Receipt #${props.payment?.id || 'unknown'}`,
      subject: 'Payment Receipt',
      creator: 'IGW Digital Printing Service',
      author: 'IGW Digital Printing Service'
    });

    // Save the PDF
    pdf.save(`receipt-${props.payment?.id || 'unknown'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.error('Failed to download receipt');
  }
};

// Replace hardcoded URL with environment variable
const GOOGLE_REVIEW_URL = import.meta.env.VITE_GOOGLE_REVIEW_URL;
if (!GOOGLE_REVIEW_URL) {
  console.error('VITE_GOOGLE_REVIEW_URL environment variable is not set');
}
</script>

<style scoped>
/* Ensure receipt is always light mode for thermal printing */
.receipt-container {
  /* Force light mode styling */
  color-scheme: light !important;
}

#printable-receipt {
  /* Force white background and black text */
  background-color: white !important;
  color: black !important;
  font-family: 'OCR-B', 'OCR B', 'PT Mono', 'Courier New', Courier, monospace;
  -webkit-font-smoothing: none;
  font-weight: 700;
}

/* Override any dark mode inheritance */
#printable-receipt * {
  color: black !important;
}

#printable-receipt .text-black {
  color: black !important;
}

@media print {
  @page {
    size: 58mm auto;  /* Standard thermal paper width */
    margin: 0;
  }

  .receipt-container {
    padding: 0;
    margin: 0;
    width: 58mm;
  }

  #printable-receipt {
    width: 58mm !important;
    max-width: 58mm !important;
    margin: 0 !important;
    -webkit-font-smoothing: none !important;
    font-weight: 700 !important;
    background-color: white !important;
    color: black !important;
  }

  .print\:hidden {
    display: none !important;
  }
}

/* Add custom font */
@font-face {
  font-family: 'OCR-B';
  src: local('OCR-B');
  font-weight: normal;
  font-style: normal;
}
</style> 